/**
 * Modern dropdowns for every native <select> in the app, without touching the forms that use them.
 *
 * The browser draws the open list of a <select> itself, in the system's plain style, and CSS cannot change it. So each
 * single-choice select on a desktop screen is given a styled button and a styled, searchable list. The real <select> stays
 * in the page (hidden), is the single source of truth, and receives a normal `input` and `change` event when someone
 * chooses, so react-hook-form's `register`, controlled `value`/`onChange` and plain uncontrolled selects all keep working.
 *
 * Left alone: phones and tablets (the native picker is better there), `multiple` and `size > 1` selects, and any select
 * marked `data-native`. Keyboard: Enter, Space or the arrow keys open it; arrows, Home, End move; Enter picks; Escape closes
 * (and does not close a dialog behind it); typing jumps to a match, and long lists get a search box.
 */

interface Item {
  value: string;
  label: string;
  disabled: boolean;
  group: string | null;
}

interface Enhanced {
  select: HTMLSelectElement;
  btn: HTMLButtonElement;
  text: HTMLSpanElement;
  signature: string;
  destroy: () => void;
}

const CHEVRON = '<svg class="ui-select-chev" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHECK = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M4.5 10.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** a list longer than this gets a search box */
export const SEARCH_FROM = 8;
const PLACEHOLDER = 'Select…';

const enhanced = new Map<HTMLSelectElement, Enhanced>();
let uid = 0;

const nativeValueSet = (() => {
  const d = typeof HTMLSelectElement !== 'undefined' ? Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value') : undefined;
  return d?.set;
})();

function itemsOf(select: HTMLSelectElement): Item[] {
  const out: Item[] = [];
  const add = (o: HTMLOptionElement, group: string | null) => out.push({ value: o.value, label: o.text.trim(), disabled: o.disabled || (o.parentElement as HTMLOptGroupElement | null)?.disabled === true, group });
  for (const child of Array.from(select.children)) {
    if (child instanceof HTMLOptionElement) add(child, null);
    else if (child instanceof HTMLOptGroupElement) for (const o of Array.from(child.children)) if (o instanceof HTMLOptionElement) add(o, child.label);
  }
  return out;
}

/** Everything the button shows, as one string, so a change anywhere is noticed with one comparison. */
const signatureOf = (s: HTMLSelectElement) =>
  [s.selectedIndex, s.options.length, s.disabled, s.classList.contains('invalid'), s.required, s.options[s.selectedIndex]?.text ?? '', s.getAttribute('aria-invalid') ?? ''].join('|');

// ---------------------------------------------------------------------------------------------------- the open list

let open: { select: HTMLSelectElement; pop: HTMLDivElement; close: () => void } | null = null;

function closeOpen() {
  open?.close();
}

function openList(e: Enhanced) {
  closeOpen();
  const { select, btn } = e;
  const items = itemsOf(select);
  const id = `ui-select-${++uid}`;
  const searchable = items.length > SEARCH_FROM;

  const pop = document.createElement('div');
  pop.className = 'ui-select-pop';
  pop.id = id;
  const search = document.createElement('input');
  if (searchable) {
    search.type = 'text';
    search.className = 'ui-select-search';
    search.placeholder = 'Search…';
    search.setAttribute('role', 'combobox');
    search.setAttribute('aria-expanded', 'true');
    search.setAttribute('aria-controls', `${id}-list`);
    search.setAttribute('aria-autocomplete', 'list');
    search.setAttribute('aria-label', 'Search the list');
    search.autocomplete = 'off';
    pop.appendChild(search);
  }
  const list = document.createElement('div');
  list.className = 'ui-select-list';
  list.id = `${id}-list`;
  list.setAttribute('role', 'listbox');
  const label = btn.getAttribute('aria-labelledby');
  if (label) list.setAttribute('aria-labelledby', label);
  pop.appendChild(list);

  let shown: Item[] = items;
  let active = Math.max(0, items.findIndex((i) => i.value === select.value && !i.disabled));
  let typed = '';
  let typedAt = 0;

  const choose = (item: Item) => {
    if (item.disabled) return;
    nativeValueSet?.call(select, item.value);
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
    sync(e);
    close();
    btn.focus();
  };

  const render = () => {
    list.textContent = '';
    if (!shown.length) {
      const none = document.createElement('div');
      none.className = 'ui-select-none';
      none.textContent = 'No matches';
      list.appendChild(none);
      return;
    }
    let group: string | null = null;
    shown.forEach((item, i) => {
      if (item.group !== null && item.group !== group) {
        const g = document.createElement('div');
        g.className = 'ui-select-group';
        g.textContent = item.group;
        g.setAttribute('role', 'presentation');
        list.appendChild(g);
      }
      group = item.group;
      const row = document.createElement('div');
      row.className = 'ui-select-opt';
      row.id = `${id}-o${i}`;
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(item.value === select.value));
      if (item.disabled) row.setAttribute('aria-disabled', 'true');
      if (i === active) row.classList.add('active');
      if (item.value === '' && item.label === '') row.classList.add('placeholder');
      const t = document.createElement('span');
      t.className = 'ui-select-opt-text';
      t.textContent = item.label || PLACEHOLDER;
      row.appendChild(t);
      if (item.value === select.value) {
        const c = document.createElement('span');
        c.className = 'ui-select-check';
        c.innerHTML = CHECK;
        row.appendChild(c);
      }
      row.addEventListener('pointermove', () => setActive(i, false));
      row.addEventListener('pointerdown', (ev) => ev.preventDefault()); // keep focus where it is
      row.addEventListener('click', () => choose(item));
      list.appendChild(row);
    });
    const cur = (searchable ? search : btn);
    const el = list.querySelector<HTMLElement>('.ui-select-opt.active');
    if (el) cur.setAttribute('aria-activedescendant', el.id);
  };

  const setActive = (i: number, scroll = true) => {
    if (!shown.length) return;
    const n = Math.min(Math.max(i, 0), shown.length - 1);
    if (n === active) return;
    list.querySelector('.ui-select-opt.active')?.classList.remove('active');
    active = n;
    const el = list.querySelectorAll<HTMLElement>('.ui-select-opt')[n];
    el?.classList.add('active');
    if (el) (searchable ? search : btn).setAttribute('aria-activedescendant', el.id);
    if (scroll) el?.scrollIntoView?.({ block: 'nearest' });
  };

  /** the next choosable row from `from` in direction `dir`, staying put when there is none */
  const step = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < shown.length; i += dir) if (!shown[i].disabled) return i;
    return from;
  };

  const place = () => {
    const r = btn.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    pop.style.minWidth = `${Math.max(r.width, 160)}px`;
    pop.style.maxWidth = `${Math.min(vw - 16, 28 * 16)}px`;
    const want = Math.min(shown.length * 36 + (searchable ? 52 : 0) + 16, 320);
    const below = vh - r.bottom - 8;
    const above = r.top - 8;
    const down = below >= want || below >= above;
    const room = Math.max(120, down ? below : above);
    list.style.maxHeight = `${Math.min(room - (searchable ? 56 : 12), 288)}px`;
    pop.style.left = `${Math.min(Math.max(8, r.left), Math.max(8, vw - Math.max(r.width, 160) - 8))}px`;
    if (down) {
      pop.style.top = `${r.bottom + 6}px`;
      pop.style.bottom = '';
      pop.dataset.side = 'down';
    } else {
      pop.style.top = '';
      pop.style.bottom = `${vh - r.top + 6}px`;
      pop.dataset.side = 'up';
    }
  };

  const onKey = (ev: KeyboardEvent) => {
    const k = ev.key;
    if (k === 'ArrowDown') { ev.preventDefault(); setActive(step(active, 1)); }
    else if (k === 'ArrowUp') { ev.preventDefault(); setActive(step(active, -1)); }
    else if (k === 'Home') { ev.preventDefault(); setActive(step(-1, 1)); }
    else if (k === 'End') { ev.preventDefault(); setActive(step(shown.length, -1)); }
    else if (k === 'PageDown') { ev.preventDefault(); setActive(step(Math.min(active + 6, shown.length - 1) - 1, 1)); }
    else if (k === 'PageUp') { ev.preventDefault(); setActive(step(Math.max(active - 6, 0) + 1, -1)); }
    else if (k === 'Enter' || (k === ' ' && !searchable)) { ev.preventDefault(); if (shown[active]) choose(shown[active]); }
    else if (k === 'Tab') close();
    else if (!searchable && k.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
      const now = Date.now();
      typed = now - typedAt > 700 ? k.toLowerCase() : typed + k.toLowerCase();
      typedAt = now;
      const hit = shown.findIndex((i) => !i.disabled && i.label.toLowerCase().startsWith(typed));
      if (hit >= 0) { ev.preventDefault(); setActive(hit); }
    }
  };

  const onSearch = () => {
    const q = search.value.trim().toLowerCase();
    shown = q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
    active = Math.max(0, shown.findIndex((i) => !i.disabled));
    render();
    place();
  };

  const onPointerDown = (ev: PointerEvent) => {
    const t = ev.target as Node;
    if (!pop.contains(t) && !btn.contains(t)) close();
  };
  const onScroll = (ev: Event) => {
    if (ev.target instanceof Node && pop.contains(ev.target)) return;
    place();
  };
  const onEscape = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape') return;
    // closes the list only: the dialog or page behind it keeps its own Escape for the next press
    ev.stopPropagation();
    ev.preventDefault();
    close();
    btn.focus();
  };
  const onResize = () => place();

  function close() {
    if (open?.pop !== pop) return;
    open = null;
    window.removeEventListener('keydown', onEscape, true);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll, true);
    document.removeEventListener('pointerdown', onPointerDown, true);
    btn.removeEventListener('keydown', onKey);
    search.removeEventListener('keydown', onKey);
    search.removeEventListener('input', onSearch);
    btn.setAttribute('aria-expanded', 'false');
    btn.removeAttribute('aria-activedescendant');
    pop.remove();
  }

  render();
  document.body.appendChild(pop);
  place();
  btn.setAttribute('aria-expanded', 'true');
  btn.setAttribute('aria-controls', `${id}-list`);
  window.addEventListener('keydown', onEscape, true);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onScroll, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  btn.addEventListener('keydown', onKey);
  if (searchable) {
    search.addEventListener('keydown', onKey);
    search.addEventListener('input', onSearch);
    search.focus();
  }
  list.querySelector<HTMLElement>('.ui-select-opt.active')?.scrollIntoView?.({ block: 'nearest' });
  open = { select, pop, close };
}

// ---------------------------------------------------------------------------------------------------- the button

function sync(e: Enhanced) {
  const { select, btn, text } = e;
  const opt = select.options[select.selectedIndex];
  const label = opt?.text.trim() ?? '';
  text.textContent = label || PLACEHOLDER;
  // an empty choice ("Choose the account", "All departments", or nothing at all) reads as a prompt, not as a value
  btn.classList.toggle('placeholder', !opt || opt.value === '');
  btn.classList.toggle('invalid', select.classList.contains('invalid') || select.getAttribute('aria-invalid') === 'true');
  btn.disabled = select.disabled;
  if (select.required) btn.setAttribute('aria-required', 'true');
  else btn.removeAttribute('aria-required');
  e.signature = signatureOf(select);
}

function eligible(select: HTMLSelectElement): boolean {
  return !select.multiple && select.size <= 1 && !('native' in select.dataset) && !select.closest('[data-native-selects]') && !enhanced.has(select);
}

function enhance(select: HTMLSelectElement) {
  if (!eligible(select)) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `ui-select-btn ${select.className}`.trim();
  const base = select.id ? `${select.id}-ui` : `ui-select-btn-${++uid}`;
  btn.id = base;
  btn.setAttribute('role', 'combobox');
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  const text = document.createElement('span');
  text.className = 'ui-select-text';
  btn.appendChild(text);
  btn.insertAdjacentHTML('beforeend', CHEVRON);

  // the name a screen reader reads: the select's own label if it has one, else the <label for=...>, plus the chosen value
  const aria = select.getAttribute('aria-label');
  const esc = (v: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v);
  const lab = select.id ? document.querySelector<HTMLLabelElement>(`label[for="${esc(select.id)}"]`) : select.closest('label');
  if (lab) {
    if (!lab.id) lab.id = `${base}-label`;
    btn.setAttribute('aria-labelledby', `${lab.id} ${base}`);
  } else if (aria) {
    btn.setAttribute('aria-label', aria);
  }
  const describedBy = select.getAttribute('aria-describedby');
  if (describedBy) btn.setAttribute('aria-describedby', describedBy);
  const title = select.getAttribute('title');
  if (title) btn.title = title;

  select.setAttribute('data-ui-select', '');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  select.insertAdjacentElement('afterend', btn);

  const e: Enhanced = { select, btn, text, signature: '', destroy: () => undefined };
  const onFocus = () => btn.focus(); // a <label for> or script focusing the select lands on the visible control
  const onChange = () => sync(e);
  select.addEventListener('focus', onFocus);
  select.addEventListener('change', onChange);
  const onBtnKey = (ev: KeyboardEvent) => {
    if (open?.select === select) return; // the open list handles its own keys
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(ev.key)) { ev.preventDefault(); openList(e); }
  };
  const onClick = () => (open?.select === select ? closeOpen() : openList(e));
  btn.addEventListener('keydown', onBtnKey);
  btn.addEventListener('click', onClick);
  e.destroy = () => {
    if (open?.select === select) closeOpen();
    select.removeEventListener('focus', onFocus);
    select.removeEventListener('change', onChange);
    btn.remove();
    select.removeAttribute('data-ui-select');
    select.removeAttribute('aria-hidden');
    select.removeAttribute('tabindex');
    enhanced.delete(select);
  };
  enhanced.set(select, e);
  sync(e);
}

function scan(root: ParentNode) {
  if (root instanceof HTMLSelectElement) enhance(root);
  else if (root instanceof Element || root instanceof Document) root.querySelectorAll('select').forEach((s) => enhance(s as HTMLSelectElement));
}

let stop: (() => void) | null = null;

/** Start enhancing (idempotent). Returns a function that puts every select back and stops watching. */
export function startSelectEnhancer(): () => void {
  if (stop || typeof document === 'undefined') return stop ?? (() => undefined);
  if (window.matchMedia?.('(pointer: coarse)').matches) return () => undefined; // phones and tablets keep the native picker

  scan(document);
  const mo = new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => { if (n instanceof Element) scan(n); });
    }
    for (const [select, e] of [...enhanced]) if (!select.isConnected) e.destroy();
  });
  mo.observe(document.body, { childList: true, subtree: true });
  // React changes the chosen option, the options and the state of a select without any DOM event, so the buttons are checked on a short timer
  const timer = window.setInterval(() => {
    for (const [select, e] of enhanced) {
      if (!select.isConnected) { e.destroy(); continue; }
      if (e.signature !== signatureOf(select)) sync(e);
    }
  }, 200);

  stop = () => {
    mo.disconnect();
    window.clearInterval(timer);
    closeOpen();
    for (const e of [...enhanced.values()]) e.destroy();
    stop = null;
  };
  return stop;
}
